import { CloudOff, LogOut, Mail, UserRound } from "lucide-react";
import { format } from "date-fns";

import { graceExpiresAt } from "@/auth/offlineSession";
import { useAuth } from "@/auth/useAuth";
import { useOnline } from "@/hooks/useOnline";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import type { SessionUser } from "@/lib/firebase";
import { cn } from "@/lib/utils";

// §13 Help & feedback. There is no backend — SlideCraft is a static bundle behind nginx —
// so the honest zero-infrastructure route is the user's own mail client. A `mailto:` sends
// from their address, which means the reply-to is right by construction and no key, quota
// or spam defence of ours is involved.
//
// What that costs, deliberately: no diagnostics ride along (the user describes their own
// problem), there is no delivery confirmation, and a machine with no mail client configured
// does nothing visible. See TODO §13 for the form this replaced. It opens in a new tab so
// the studio is never navigated away from — see the anchor below.
const FEEDBACK_EMAIL = "contact@deckai.co";
const FEEDBACK_MAILTO = `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent(
  "DeckAI feedback",
)}`;

// Initials for the avatar fallback (§9.3.1): first + last initial of the display name, else
// the first letter of the email.
function initials(user: SessionUser): string {
  const name = user.displayName?.trim();
  if (name) {
    const parts = name.split(/\s+/);
    return (
      parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")
    ).toUpperCase();
  }
  return user.email?.trim()?.[0]?.toUpperCase() ?? "?";
}

// §9.3 Account control for the far right of the TopBar. Three states:
//   loading → a neutral skeleton circle so the corner doesn't flip Sign-in → avatar on load;
//   signed out → a Sign in button that opens the shared dialog (§9.2);
//   signed in → the avatar, opening a dropdown with name/email, Help & feedback, Sign out.
export interface AccountMenuProps {
  // §14.7 In the desktop shell this control sits in the tab row rather than in a full-width
  // header, so signed-out it is a single person icon instead of a "Sign in" button: that row
  // is window chrome, not a place to advertise an account.
  iconOnly?: boolean;
  // `sm` matches the other icon buttons in the tab row; `default` is the web header's size.
  size?: "sm" | "default";
}

export function AccountMenu({
  iconOnly = false,
  size = "default",
}: AccountMenuProps = {}) {
  const {
    user,
    status,
    signOut,
    promptSignIn,
    tokenFresh,
    lastVerifiedAt,
    withinOfflineGrace,
  } = useAuth();
  // The line below says "offline", so only show it when that is actually what happened —
  // otherwise the moment between resolving the session and minting the first token would
  // flash it at every signed-in user on a perfectly good connection.
  const online = useOnline();

  const box = size === "sm" ? "h-7 w-7" : "h-9 w-9";

  // §9.3.3 Hold a placeholder until Firebase resolves the persisted session.
  if (status === "loading") {
    return (
      <Skeleton
        className={cn(
          "rounded-full",
          box,
          iconOnly ? "bg-slate-200" : "bg-slate-700",
        )}
      />
    );
  }

  // §9.3.2 Signed out.
  if (status !== "in" || !user) {
    return iconOnly ? (
      <Button
        variant="ghost"
        size="icon"
        className={cn(box, "text-slate-600")}
        aria-label="Sign in"
        title="Sign in"
        onClick={() => promptSignIn()}
      >
        <UserRound className="h-4 w-4" />
      </Button>
    ) : (
      <Button variant="secondary" size="sm" onClick={() => promptSignIn()}>
        Sign in
      </Button>
    );
  }

  // §9.3.1/§9.3.2 Signed in — avatar → dropdown.
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account"
          className={cn(
            "rounded-full outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
            iconOnly ? "ring-offset-1" : "ring-offset-2 ring-offset-slate-900",
          )}
        >
          <Avatar className={box}>
            {user.photoURL && (
              <AvatarImage
                src={user.photoURL}
                alt={user.displayName ?? user.email ?? "Account"}
              />
            )}
            <AvatarFallback
              className={cn(
                "bg-slate-700 font-medium text-white",
                size === "sm" ? "text-xs" : "text-sm",
              )}
            >
              {initials(user)}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          {user.displayName && (
            <span className="truncate text-sm font-medium">
              {user.displayName}
            </span>
          )}
          {user.email && (
            <span className="truncate text-xs font-normal text-slate-500">
              {user.email}
            </span>
          )}
        </DropdownMenuLabel>
        {/* §14.6.7 Say the offline state out loud. Signed in with no usable bearer is a real
            state (§14.6.3) and the user cannot see it any other way — the avatar looks
            identical — so the expiry is never a surprise mid-flight. "at most", because the
            Library's cache is evictable (§14.2.7) and can end offline premium early. */}
        {status === "in" && !tokenFresh && !online && (
          <>
            <DropdownMenuSeparator />
            <div className="flex gap-2 px-2 py-1.5 text-xs text-slate-500">
              <CloudOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="flex flex-col gap-0.5">
                <span>{offlineSince(lastVerifiedAt)}</span>
                <span>
                  {withinOfflineGrace
                    ? `Library slides you already have stay available until at most ${offlineUntil(user.uid)}.`
                    : "Reconnect once to open Gridd Library slides again. Your own documents are unaffected."}
                </span>
              </span>
            </div>
          </>
        )}
        <DropdownMenuSeparator />
        {/* §13 `asChild` so the item renders as a real anchor: a plain onClick handler would
            lose middle-click, "copy link", and the browser's own no-mail-handler behaviour.

            `target="_blank"` matters here even though a mailto normally hands off to the OS.
            When the browser's mail handler is *webmail* (Gmail registered via
            registerProtocolHandler), a same-tab mailto navigates the studio away — losing the
            open deck's editor state and every live embed. Opening in a new tab keeps the
            studio exactly where it was. */}
        <DropdownMenuItem asChild>
          <a href={FEEDBACK_MAILTO} target="_blank" rel="noopener noreferrer">
            <Mail className="mr-2 h-4 w-4" /> Help &amp; feedback
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void signOut()}>
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// A stored timestamp can be missing (a session restored on a machine that never reached the
// network) — say something true in that case rather than rendering "Invalid Date".
function offlineSince(at: number | null): string {
  if (at === null) return "Signed in · offline";
  return `Signed in · offline since ${format(new Date(at), "EEEE HH:mm")}`;
}

function offlineUntil(uid: string): string {
  const at = graceExpiresAt(uid);
  return at === null
    ? "your next connection"
    : format(new Date(at), "EEEE HH:mm");
}
